import { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getDeposits, createDeposit, getStaff, deleteDeposit } from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { SearchableSelect } from '../components/ui/searchable-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, ArrowUp, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import OfficeLedgerTab from '../components/OfficeLedgerTab';
import StaffLedgerTab from '../components/StaffLedgerTab';

export default function DepositsPage() {
  const { user } = useAuth();
  const [deposits, setDeposits] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState('all');
  const [formData, setFormData] = useState({ staff_id: '', amount: '', notes: '' });
  const [deletingId, setDeletingId] = useState(null);

  const role = user?.role;
  const isOwner = role === 'admin' || role === 'owner';
  const isAdminOrAbove = isOwner || role === 'assistant';
  const isAdmin = isAdminOrAbove;
  const isAccountant = role === 'accountant';
  // Deposits tab = Owner/Admin/Assistant. Office Ledger tab = Owner/Admin + CA only.
  // Staff Ledger tab = Owner/Admin + Assistant + CA (audit view).
  const canSeeDeposits = isAdminOrAbove;
  const canSeeOfficeLedger = isOwner || isAccountant;
  const canSeeStaffLedger = isOwner || role === 'assistant' || isAccountant;
  const [activeTab, setActiveTab] = useState(canSeeDeposits ? 'deposits' : (canSeeStaffLedger ? 'staff' : 'office'));

  const fetchData = useCallback(async () => {
    if (!isAdmin) return;
    try {
      setLoading(true);
      const staffId = selectedStaff === 'all' ? undefined : selectedStaff;
      const { data } = await getDeposits(staffId);
      setDeposits(data);
      
      if (isAdmin) {
        const staffRes = await getStaff();
        setStaffList(staffRes.data);
      }
    } catch (error) {
      toast.error('Failed to fetch deposits');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, selectedStaff]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!canSeeDeposits && !canSeeOfficeLedger && !canSeeStaffLedger) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createDeposit({
        staff_id: isAdmin ? formData.staff_id : user.id,
        amount: parseFloat(formData.amount),
        notes: formData.notes || null
      });
      toast.success('Deposit added');
      setDialogOpen(false);
      setFormData({ staff_id: '', amount: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add deposit');
    }
  };

  const handleDelete = async (deposit) => {
    if (!isOwner) {
      toast.error('Only Owner/Admin can reverse a deposit');
      return;
    }
    const ok = window.confirm(
      `⚠️ Reverse this deposit?\n\n` +
      `Staff: ${deposit.staff_name}\n` +
      `Amount: ₹${deposit.amount.toLocaleString('en-IN')}\n` +
      `Notes: ${deposit.notes || '-'}\n\n` +
      `Staff Ledger se transaction reverse ho jayega. Continue?`
    );
    if (!ok) return;
    try {
      setDeletingId(deposit.id);
      const { data } = await deleteDeposit(deposit.id);
      toast.success(data?.message || 'Deposit reversed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reverse deposit');
    } finally {
      setDeletingId(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const totalDeposits = deposits.reduce((sum, d) => sum + d.amount, 0);

  return (
    <div className="space-y-6" data-testid="deposits-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="text-2xl font-bold text-gray-900">
          {activeTab === 'office' ? 'Office Ledger' : (activeTab === 'staff' ? 'Staff Ledger' : 'Deposits')}
        </h1>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200" data-testid="deposits-tabs">
        {canSeeDeposits && (
          <button
            onClick={() => setActiveTab('deposits')}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${
              activeTab === 'deposits'
                ? 'border-[#002FA7] text-[#002FA7]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid="tab-deposits"
          >
            Deposits
          </button>
        )}
        {canSeeStaffLedger && (
          <button
            onClick={() => setActiveTab('staff')}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${
              activeTab === 'staff'
                ? 'border-[#002FA7] text-[#002FA7]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid="tab-staff-ledger"
          >
            Staff Ledger
          </button>
        )}
        {canSeeOfficeLedger && (
          <button
            onClick={() => setActiveTab('office')}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${
              activeTab === 'office'
                ? 'border-[#002FA7] text-[#002FA7]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid="tab-office-ledger"
          >
            Office Ledger
          </button>
        )}
      </div>

      {activeTab === 'office' ? (
        <OfficeLedgerTab />
      ) : activeTab === 'staff' ? (
        <StaffLedgerTab />
      ) : (
        <>
          <div className="flex items-center justify-end gap-4">
            {isAdmin && (
              <SearchableSelect
                options={[
                  { value: 'all', label: 'All Staff' },
                  ...staffList.map(s => ({ value: s.id, label: s.name, sublabel: s.email }))
                ]}
                value={selectedStaff}
                onValueChange={setSelectedStaff}
                placeholder="Select Staff"
                searchPlaceholder="Search staff..."
                className="w-48"
                data-testid="staff-filter-deposits"
              />
            )}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="add-deposit-btn">
                  <Plus size={18} className="mr-2" /> Add Deposit
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Deposit</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {isAdmin && (
                    <div>
                      <Label className="form-label">Staff</Label>
                      <SearchableSelect
                        options={staffList.map(s => ({ value: s.id, label: s.name, sublabel: s.email }))}
                        value={formData.staff_id}
                        onValueChange={(v) => setFormData({ ...formData, staff_id: v })}
                        placeholder="Select Staff"
                        searchPlaceholder="Search staff..."
                        data-testid="deposit-staff-select"
                      />
                    </div>
                  )}
                  <div>
                    <Label className="form-label">Amount (₹)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      className="form-input"
                      placeholder="e.g., 5000"
                      required
                      data-testid="deposit-amount-input"
                    />
                  </div>
                  <div>
                    <Label className="form-label">Notes (Optional)</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="form-input"
                      placeholder="Any notes about this deposit"
                      data-testid="deposit-notes-input"
                    />
                  </div>
                  <Button type="submit" className="w-full btn-primary" data-testid="deposit-submit-btn">
                    Add Deposit
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Summary Card */}
          <div className="stat-card flex items-center justify-between" data-testid="deposits-summary">
            <div>
              <div className="stat-label">Total Deposits</div>
              <div className="stat-value text-green-600">{formatCurrency(totalDeposits)}</div>
            </div>
            <ArrowUp size={32} className="text-green-500" />
          </div>

          <div className="card-flat overflow-x-auto">
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : (
              <Table className="table-dense">
                <TableHeader>
                  <TableRow>
                    {isAdmin && <TableHead>Staff</TableHead>}
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead>Date</TableHead>
                    {isOwner && <TableHead className="text-right w-20">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {deposits.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isOwner ? (isAdmin ? 5 : 4) : (isAdmin ? 4 : 3)} className="text-center text-gray-500">
                        No deposits found
                      </TableCell>
                    </TableRow>
                  ) : (
                    deposits.map((d) => (
                      <TableRow key={d.id} data-testid={`deposit-row-${d.id}`}>
                        {isAdmin && <TableCell className="font-medium">{d.staff_name}</TableCell>}
                        <TableCell className="numeric text-green-600">+{formatCurrency(d.amount)}</TableCell>
                        <TableCell className="text-gray-500">{d.notes || '-'}</TableCell>
                        <TableCell>{formatDateTime(d.created_at)}</TableCell>
                        {isOwner && (
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(d)}
                              disabled={deletingId === d.id}
                              className="h-8 w-8 p-0 text-red-600 hover:bg-red-50 hover:text-red-700"
                              title="Reverse this deposit"
                              data-testid={`deposit-delete-${d.id}`}
                            >
                              <Trash size={16} />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
